"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { DropdownMenu } from "../input/dropdown-menu";
import { MoreHorizontal } from "lucide-react";

export type MoreMenuOption = {
  id: string;
  label: string;
  onClick: (item: any) => void;
  disable?: boolean;
  icon?: React.ReactNode;
  variant?: "secondary" | "danger" | "primary" | "none";
};

export type ListingItemProps = {
  item: any;
  id?: string | number;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  meta?: React.ReactNode;
  columns?: React.ReactNode;
  actions?: React.ReactNode;
  moreOptions?: MoreMenuOption[];
  hideMoreBtn?: boolean;
  className?: string;
  dataTestId?: string;
  selected?: boolean;
  onSelect?: (id: string | number | undefined, selected: boolean) => void;
  onClick?: () => void;
  children?: React.ReactNode;
};

export const ListingItem = React.forwardRef<HTMLDivElement, ListingItemProps>(
  (
    {
      item,
      id,
      icon,
      title,
      meta,
      columns,
      actions,
      moreOptions,
      hideMoreBtn,
      className,
      dataTestId,
      selected,
      onSelect,
      onClick,
      children,
    },
    ref
  ) => {
    const testId = dataTestId ?? (id ? `listing-item-${id}` : "listing-item");
    return (
      <div
        ref={ref}
        className={cn(styleDrivers.listing.itemRow, className)}
        data-test-id={`${testId}-container`}
        tabIndex={0}
        onClick={onClick}
      >
        {icon ? (
          <div className={cn(styleDrivers.listing.itemIcon)}>{icon}</div>
        ) : null}
        <div className="space-y-1 min-w-0">
          {title ? (
            <div className={cn(styleDrivers.listing.itemTitle)}>{title}</div>
          ) : null}
          {meta ? (
            <div className={cn(styleDrivers.listing.itemMeta)}>{meta}</div>
          ) : null}
          {children}
        </div>
        <div className={cn(styleDrivers.listing.itemActions)}>
          {columns}
          {actions}
          {!hideMoreBtn ? (
            <DropdownMenu
              trigger={{
                icon: <MoreHorizontal className="h-4 w-4" />,
                iconOnly: true,
                variant: "outline",
                size: "sm"
              }}
              items={moreOptions?.map((opt) => ({
                type: 'item' as const,
                label: opt.label,
                icon: opt.icon,
                disabled: opt.disable,
                onClick: () => {
                  if (opt.disable) return;
                  opt.onClick(item);
                }
              })) || []}
              align="end"
              side="bottom"
            />
          ) : null}
        </div>
      </div>
    );
  }
);

ListingItem.displayName = "ListingItem";

export default ListingItem;


